// src/authContext.js
// Muster: 1:1 aus dem CRM (MeinKleinesCRM) übernommen. Passt unveraendert, weil
// DatenLeserWeb.Auth.AuthService.GenerateJwtToken dieselben Claim-Typen verwendet
// (ClaimTypes.NameIdentifier, ClaimTypes.Role) wie das CRM-Backend.
//
// Achtung Sonderfall Rolle "System": deren Token trägt ZWEI Role-Claims ("System" UND
// "Admin", siehe AuthService.GenerateJwtToken - System ist zugleich Admin). jwt-decode liefert
// bei mehreren gleichnamigen Claims ein ARRAY statt eines Strings zurück - "role" wird deshalb
// immer zu einem Array normalisiert (rollen) und "role" bleibt nur die erste/primäre Rolle für
// Anzeigezwecke. Prüfungen auf eine bestimmte Rolle MÜSSEN hatRolle(...) verwenden, nicht
// "role === '...'" - sonst würde der System-Benutzer z.B. beim Admin-Check durchfallen.
//
// Zusätzlich: dokumentKontingent/dokumentVerbraucht (nur für Rolle "Tester" gesetzt, sonst
// null/0) werden über GET /api/auth/me geladen - aktualisiereKontingent() rufen die
// Extraktions-Hooks (useDokumentExtraktion, useStapelverarbeitung) nach jedem Extraktions-
// versuch auf, damit die Anzeige immer den serverseitigen (autoritativen) Stand zeigt.
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from './api';

const AuthContext = createContext();

// Normalisiert den Role-Claim aus dem decodierten Token auf ein Array - egal ob er als
// einzelner String oder (bei mehreren Role-Claims, siehe oben) als Array ankommt.
function extrahiereRollen(decodedToken) {
  const rawRole =
    decodedToken.role ||
    decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
  if (!rawRole) return [];
  return Array.isArray(rawRole) ? rawRole : [rawRole];
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(sessionStorage.getItem('token') || null);
  const [role, setRole] = useState(null);
  const [rollen, setRollen] = useState([]);
  const [userId, setUserId] = useState(null);
  const [dokumentKontingent, setDokumentKontingent] = useState(null);
  const [dokumentVerbraucht, setDokumentVerbraucht] = useState(0);

  const aktualisiereKontingent = useCallback(async (aktuellerToken) => {
    const nutzeToken = aktuellerToken || token;
    if (!nutzeToken) return;
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${nutzeToken}` }
      });
      setDokumentKontingent(response.data.dokumentKontingent);
      setDokumentVerbraucht(response.data.dokumentVerbraucht);
    } catch (error) {
      console.error('Kontingent konnte nicht geladen werden:', error);
    }
  }, [token]);

  // Rolle(n) und User-ID aus dem Token extrahieren
  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        const rollenArray = extrahiereRollen(decodedToken);
        setRollen(rollenArray);
        setRole(rollenArray[0] ?? null);

        const userIdFromToken = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
        setUserId(userIdFromToken ? parseInt(userIdFromToken, 10) : null);

        setIsAuthenticated(true);
        aktualisiereKontingent(token);
      } catch (error) {
        console.error('Fehler beim Decodieren des Tokens:', error);
        setIsAuthenticated(false);
        setRole(null);
        setRollen([]);
        setUserId(null);
      }
    } else {
      setIsAuthenticated(false);
      setRole(null);
      setRollen([]);
      setUserId(null);
      setDokumentKontingent(null);
      setDokumentVerbraucht(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Event-Listener fuer beforeunload (Seite wird neu geladen oder Tab geschlossen)
  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.removeItem('token');
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const login = (newToken) => {
    sessionStorage.setItem('token', newToken);
    setToken(newToken);
    try {
      const decodedToken = jwtDecode(newToken);
      const rollenArray = extrahiereRollen(decodedToken);
      setRollen(rollenArray);
      setRole(rollenArray[0] ?? null);

      const userIdFromToken = decodedToken['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'];
      setUserId(userIdFromToken ? parseInt(userIdFromToken, 10) : null);

      setIsAuthenticated(true);
      aktualisiereKontingent(newToken);
    } catch (error) {
      console.error('Fehler beim Decodieren des Tokens:', error);
      setIsAuthenticated(false);
      setRole(null);
      setRollen([]);
      setUserId(null);
    }
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    setRole(null);
    setRollen([]);
    setUserId(null);
    setDokumentKontingent(null);
    setDokumentVerbraucht(0);
    setIsAuthenticated(false);
  };

  // Bevorzugt statt "role === '...'" verwenden - berücksichtigt auch mehrere Role-Claims
  // (aktuell nur beim System-Benutzer der Fall: rollen = ['System', 'Admin']).
  const hatRolle = (gesuchteRolle) => rollen.includes(gesuchteRolle);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        token,
        role,
        rollen,
        hatRolle,
        userId,
        dokumentKontingent,
        dokumentVerbraucht,
        aktualisiereKontingent,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);